import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import translations from '../locales/translations.json';
import { readLanguagePreference, saveLanguagePreference } from '../utils/languagePreference';

export type Language = 'en' | 'fr';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

interface TranslationProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
  onLanguageChange?: (language: Language) => void;
  useSavedLanguage?: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

function isTranslationRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNestedTranslation(obj: Record<string, unknown>, path: string): string {
  const value = path.split('.').reduce<unknown>((current, key) => {
    if (!isTranslationRecord(current)) {
      return undefined;
    }

    return current[key];
  }, obj);

  return typeof value === 'string' ? value : path;
}

function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('fr')) {
    return 'fr';
  }
  return 'en';
}

function readPreferredLanguage(): Language {
  return readLanguagePreference() ?? detectBrowserLanguage();
}

function applyDocumentLanguage(language: Language): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

export function TranslationProvider({
  children,
  initialLanguage,
  onLanguageChange,
  useSavedLanguage = true,
}: TranslationProviderProps) {
  const [language, setLanguage] = useState<Language>(() => {
    return initialLanguage ?? (useSavedLanguage ? readPreferredLanguage() : detectBrowserLanguage());
  });

  /**
   * Keeps the first client render identical to prerendered markup, then applies
   * the user's saved or browser language after hydration.
   */
  useEffect(() => {
    if (!useSavedLanguage) {
      applyDocumentLanguage(language);
      return;
    }

    const preferredLanguage = readPreferredLanguage();
    applyDocumentLanguage(preferredLanguage);

    if (preferredLanguage !== language) {
      setLanguage(preferredLanguage);
    }
  }, [language, useSavedLanguage]);

  const updateLanguage = useCallback((nextLanguage: Language) => {
    if (onLanguageChange) {
      saveLanguagePreference(nextLanguage);
      onLanguageChange(nextLanguage);
      return;
    }

    setLanguage(nextLanguage);
    applyDocumentLanguage(nextLanguage);

    if (useSavedLanguage) {
      saveLanguagePreference(nextLanguage);
    }
  }, [onLanguageChange, useSavedLanguage]);

  const t = (key: string): string => {
    return getNestedTranslation(translations[language], key);
  };

  return (
    <TranslationContext.Provider value={{ language, setLanguage: updateLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
}
